import { useEffect, useState } from "react"
import { useNavigate, useLocation, useSearchParams } from "react-router-dom"
import { createDevShortcutSession } from "~/api/assessmentSessions"
import type { AssessmentSessionResponse } from "~/api/assessmentSessions"
import { assetUrl } from "~/utils/assetUrl"
import { PageShell } from "~/components/PageShell"

type RevealState = { sessionToken?: string; result?: AssessmentSessionResponse } | null

export function RevealPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const state = location.state as RevealState
  const [devResult, setDevResult] = useState<AssessmentSessionResponse | null>(null)
  const result = state?.result ?? devResult
  const sessionToken = state?.sessionToken ?? devResult?.session_token

  const devType = import.meta.env.DEV && !state?.result && !devResult ? searchParams.get("type") : null

  useEffect(() => {
    if (devType) {
      createDevShortcutSession(devType, searchParams.get("name") ?? undefined).then(setDevResult)
      return
    }
    if (result) return
    const path = sessionToken
      ? `/results?token=${encodeURIComponent(sessionToken)}`
      : "/results"
    navigate(path, { state: state ?? undefined, replace: true })
  }, [result, sessionToken, state, navigate, devType, searchParams])

  function handleContinue() {
    const path = sessionToken
      ? `/results?token=${encodeURIComponent(sessionToken)}`
      : "/results"
    navigate(path, { state: state ?? undefined })
  }

  if (!result) {
    return (
      <PageShell>
        <p className="pt-4 text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  const dogName = result.dog_name
  const archetype = result.archetype

  const archetypeName = archetype?.name ?? result.archetype_label ?? "your dog"
  const primaryHook = archetype?.hook_primary ?? result.archetype_explanation ?? ""
  const secondaryHook = archetype?.hook_secondary ?? ""
  const iconPath = archetype ? assetUrl(`/images/archetypes/logos/${archetype.icon}`) : null

  return (
    <PageShell>
      <div className="flex w-full max-w-md flex-col items-center text-center">
        {iconPath && (
          <div className="flex aspect-square w-full max-w-[120px] items-center justify-center rounded-xl bg-[var(--color-surface-card)]">
            <img
              src={iconPath}
              alt={archetypeName}
              className="h-20 w-20 max-h-full max-w-full object-contain"
            />
          </div>
        )}

        <h1 className="mt-7 text-3xl font-semibold leading-tight text-[var(--color-text-primary)]">
          Meet {dogName} {archetypeName}
        </h1>

        <p className="mt-3 text-base font-medium text-[var(--color-text-primary)]">
          Your dog&apos;s personality is just the beginning.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-[var(--color-text-secondary)]">
          Keep exploring below for personalised guidance, answers and tips to build a deeper bond
          with {dogName}.
        </p>
        <p className="mt-4 text-base leading-relaxed text-[var(--color-text-secondary)]">
          {primaryHook || "Your dog's unique personality profile is ready."}
        </p>
        {secondaryHook && (
          <p className="mt-3 text-base leading-relaxed text-[var(--color-text-secondary)]">
            {secondaryHook}
          </p>
        )}

        <button
          type="button"
          onClick={handleContinue}
          className="mt-5 rounded-lg bg-[var(--color-text-primary)] px-6 py-3 font-medium text-[var(--color-surface-card)] transition hover:opacity-90"
        >
          Start Understanding {dogName}
        </button>
      </div>
    </PageShell>
  )
}
